import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Radio, ArrowUpRight, ArrowDownLeft } from 'lucide-react';
import Card from '../common/Card';
import { useSocketContext } from '../../App';
import { useApi } from '../../hooks/useApi';
import { getTransactions } from '../../api/client';
import { formatDistanceToNow } from 'date-fns';

export default function TransactionFeed() {
  const navigate = useNavigate();
  const socket = useSocketContext();
  const { data: initialTxns } = useApi(getTransactions, { limit: 30 });
  const [transactions, setTransactions] = useState([]);
  const feedRef = useRef(null);

  // Load initial transactions
  useEffect(() => {
    if (initialTxns) {
      const txns = Array.isArray(initialTxns) ? initialTxns : initialTxns.data || [];
      setTransactions(txns.slice(0, 50));
    }
  }, [initialTxns]);

  // Add real-time transactions
  useEffect(() => {
    if (socket?.lastTransaction) {
      setTransactions((prev) => [socket.lastTransaction, ...prev].slice(0, 50));
    }
  }, [socket?.lastTransaction]);

  // Auto-scroll to top on new transaction
  useEffect(() => {
    if (feedRef.current) {
      feedRef.current.scrollTop = 0;
    }
  }, [transactions[0]]);

  return (
    <Card
      title="Live Transactions"
      icon={Radio}
      glow={socket?.isConnected ? 'green' : 'none'}
      className="h-full flex flex-col"
    >
      <div className="flex items-center gap-2 mb-3">
        <div
          className={`w-1.5 h-1.5 rounded-full ${
            socket?.isConnected ? 'bg-accent-green animate-pulse' : 'bg-accent-red'
          }`}
        />
        <span className="text-[10px] font-mono text-text-muted uppercase">
          {socket?.isConnected ? 'Live Feed' : 'Offline — showing cached'}
        </span>
      </div>

      <div
        ref={feedRef}
        className="flex-1 overflow-y-auto space-y-1 max-h-[340px] min-h-[200px]"
      >
        {transactions.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-text-muted text-xs">
            No transactions yet
          </div>
        ) : (
          transactions.map((tx, idx) => {
            const isSell = (tx.transaction_type || tx.type) === 'SELL';
            const isNew = idx === 0;

            return (
              <div
                key={tx.id || tx._id || `${tx.created_at}-${idx}`}
                className={`flex items-center gap-3 px-3 py-2 rounded transition-all duration-200 hover:bg-terminal-border/30 cursor-pointer ${
                  isNew ? 'animate-slide-down' : ''
                }`}
                onClick={() => (tx.item_classname || tx.classname) && navigate(`/items/${tx.item_classname || tx.classname}`)}
              >
                {/* Icon */}
                <div
                  className={`w-6 h-6 rounded flex items-center justify-center shrink-0 ${
                    isSell
                      ? 'bg-accent-green/10 text-accent-green'
                      : 'bg-accent-red/10 text-accent-red'
                  }`}
                >
                  {isSell ? (
                    <ArrowUpRight className="w-3.5 h-3.5" />
                  ) : (
                    <ArrowDownLeft className="w-3.5 h-3.5" />
                  )}
                </div>

                {/* Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-text-primary truncate font-medium">
                      {tx.display_name || tx.item_classname || tx.classname || 'Unknown Item'}
                    </span>
                    <span
                      className={`text-[10px] font-mono ${
                        isSell ? 'text-accent-green' : 'text-accent-red'
                      }`}
                    >
                      {isSell ? 'SELL' : 'BUY'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] text-text-muted font-mono">
                      {tx.quantity || 1}x
                    </span>
                    <span className="text-[10px] text-text-muted">·</span>
                    <span className="text-[10px] text-text-muted font-mono">
                      ₽{(tx.price_per_unit || tx.price || 0).toLocaleString()}
                    </span>
                    <span className="text-[10px] text-text-muted">·</span>
                    <span className="text-[10px] text-text-muted truncate">
                      {tx.zone_name || tx.zone || '—'}
                    </span>
                  </div>
                </div>

                {/* Time */}
                <span className="text-[10px] text-text-muted font-mono shrink-0">
                  {(tx.created_at || tx.timestamp)
                    ? formatDistanceToNow(new Date(tx.created_at || tx.timestamp), { addSuffix: true })
                    : '—'}
                </span>
              </div>
            );
          })
        )}
      </div>
    </Card>
  );
}
